// src/lib/plan-config.ts
// Central plan configuration — feature sets, pricing, and limits for every tier.
//
// History: BASIC (#420) and PREMIUM (packaging v2) were both retired. The
// TypeScript type below narrows to the 2 active tiers; legacy DB rows with
// subscriptionPlan='BASIC' coerce to FREE and 'PREMIUM' coerce to PRO at the
// boundary (see getEffectivePlan in effective-plan.ts). The Prisma enum still
// includes BASIC + PREMIUM for historical-data compatibility — dropping them
// would require a multi-step Postgres migration and is intentionally deferred.
//
// Packaging v2: one paid tier (Pro), priced by billing term. Sage is available
// to everyone, metered by tier (Free 3/day · Pro 50/day · Admin unlimited).

import { countPhrase } from './count-phrase'

export type PlanKey = 'FREE' | 'PRO'

export interface PlanConfig {
  /** Display name for UI */
  name: string
  /** Monthly price in USD (0 for free) */
  price: number
  /** Max SAT tests per calendar month (Infinity = unlimited; 0 = none) */
  testsPerMonth: number
  /**
   * Checkpoints a FREE user may finish per calendar month.
   *
   * Deliberately a CADENCE limit, not a quantity one: a Checkpoint is assembled
   * fresh from the bank every time, so there is no inventory to protect — only
   * a rhythm to set. It exists so a free student can WATCH THEIR SCORE MOVE,
   * which is the product's whole claim and the thing a single measurement can
   * never show.
   *
   * A Checkpoint returns a RANGE, never an exact scaled score, so this gives
   * away the experience of progress without giving away the instrument.
   */
  checkpointsPerMonth: number
  /**
   * Benchmarks a plan may take IN TOTAL — not per month.
   *
   * A benchmark is a frozen form: finite, expensive content held out of the
   * practice pool forever. So unlike every other allowance here this one is a
   * LIFETIME count.
   *
   * The free tier's 1 is not an arbitrary quota. A trend needs two points, so
   * one benchmark structurally shows a student WHERE THEY STAND and cannot show
   * whether it is working — the honest shape of the upgrade, not a wall.
   */
  benchmarksTotal: number
  /** Max drill practice questions per calendar day (Infinity = unlimited) */
  drillQuestionsPerDay: number
  /**
   * Ordered list of features for the pricing page — WITHOUT the Benchmark,
   * which is conditional (see planFeatures). Render planFeatures(), not this.
   */
  features: string[]
  /** Short tagline for the pricing card */
  tagline: string
}

// Free's measurement allowances, named once so the limits below AND the
// pricing-card features are the same numbers (the features used to be typed
// out by hand, and copy elsewhere drifted from them — see plan-copy.ts).
const FREE_LIMITS = {
  // Packaging v2: 1 free full-length practice test per month (the OnePrep-style
  // free-test hook) — the 2nd+ in a month upsells to Pro (unlimited).
  testsPerMonth: 1,
  checkpointsPerMonth: 1,
  benchmarksTotal: 1,
} as const

export const PLANS: Record<PlanKey, PlanConfig> = {
  FREE: {
    name: 'Free',
    price: 0,
    ...FREE_LIMITS,
    // Caps the DAILY MIXED DRILL only (12 questions, no topic choice).
    //
    // It does NOT cap topic practice, which is FREE and unlimited for everyone
    // — see the policy comments in /api/practice/start and PracticeClient. The
    // open bank is deliberate: it's the acquisition surface, and it matches the
    // free question bank that is our main rival's strongest draw. What Pro sells
    // is not access to questions but knowing WHICH ones to do — the adaptive
    // engine, the study plan, and the analytics below.
    //
    // (This comment previously claimed topic practice was paid. It never was
    // under packaging v2, and the stale wording sits in the file that calls
    // itself the source of truth for tiers, so it misled a reader into
    // designing around a Free tier that doesn't exist.)
    drillQuestionsPerDay: 12,
    tagline: 'See where you stand. Upgrade when you’re ready to prep.',
    features: [
      'Free diagnostic test',
      'Practice the full question bank by topic — unlimited',
      `${countPhrase(FREE_LIMITS.testsPerMonth, 'full-length practice test', { numerals: true })} a month`,
      // The point of this one is WATCHING THE NUMBER MOVE. A single measurement
      // cannot show improvement, and improvement is the product's whole claim.
      `${countPhrase(FREE_LIMITS.checkpointsPerMonth, 'Checkpoint', { numerals: true })} a month — re-measure and see your range move`,
      // No Benchmark line here: it is added by planFeatures() only while a
      // Benchmark is actually open to students (BENCHMARK_FEATURES below).
      // Packaging v2: a daily taste of Sage (the quality wedge) on the free tier.
      'AI Tutor (Sage) — 3 questions a day to try',
      // 0.4 re-tier: lead the free tier with the QUALITY wedge — our verified,
      // expert-checked graphical explanations (the counter to rivals' "AI slop").
      'Verified, step-by-step explanations (with diagrams)',
    ],
  },
  PRO: {
    name: 'Pro',
    price: 24.99,
    testsPerMonth: Infinity,
    checkpointsPerMonth: Infinity,
    benchmarksTotal: Infinity,
    drillQuestionsPerDay: Infinity,
    tagline: 'Everything you need to seriously prep — with your AI tutor.',
    features: [
      'Unlimited practice tests + Checkpoints — no monthly wait',
      'Adaptive practice that targets your weak spots',
      // Packaging v2: one paid tier, fair-use cap (50/day covers normal study).
      'AI Tutor (Sage) on every question — fair use, 50/day',
      'Full analytics: Mistake DNA, leverage, pacing, mastery',
      'Personalized study plan',
      'Spaced repetition review',
      'PDF score reports',
      'Email & community support',
    ],
  },
}

/**
 * The Benchmark line on each pricing card.
 *
 * Conditional, unlike every other feature: the Benchmark is held behind a
 * release flag AND a per-form release (benchmark-access.ts), and while either is
 * shut a student cannot find one anywhere. Pricing used to promise it anyway, so
 * the line is now added only when isBenchmarkAvailable() says a student could
 * sit one (benchmark-availability.ts). Worded from the same allowance numbers.
 */
export const BENCHMARK_FEATURES: Record<PlanKey, string> = {
  FREE: `${countPhrase(FREE_LIMITS.benchmarksTotal, 'Benchmark', { numerals: true })} — a fixed full-length paper with one exact, comparable score`,
  PRO: 'Every Benchmark paper — exact scores you can compare over time',
}

/**
 * A plan's pricing-card features, with the Benchmark line only when a Benchmark
 * is available to students. It sits right after the Checkpoint line (the other
 * measurement it is compared with), or at the end if there is none.
 */
export function planFeatures(
  plan: PlanKey,
  { benchmarkAvailable }: { benchmarkAvailable: boolean },
): string[] {
  const base = PLANS[plan].features
  if (!benchmarkAvailable) return [...base]
  const at = base.findIndex((f) => /Checkpoint/.test(f))
  const out = [...base]
  out.splice(at === -1 ? out.length : at + 1, 0, BENCHMARK_FEATURES[plan])
  return out
}

/**
 * All plan keys in tier order (lowest to highest). Used for rank
 * comparisons via planRank() and for iterating the tiers on the
 * pricing page.
 */
export const PLAN_ORDER: PlanKey[] = ['FREE', 'PRO']

/**
 * Numeric rank for a plan — higher = more privileged.
 * Useful for upgrade/downgrade comparisons.
 */
export function planRank(plan: PlanKey): number {
  return PLAN_ORDER.indexOf(plan)
}

/** Return true if `plan` is at or above `requiredPlan`. */
export function hasPlanAccess(plan: PlanKey, requiredPlan: PlanKey): boolean {
  return planRank(plan) >= planRank(requiredPlan)
}

/**
 * Feature gating — declarative map of feature → minimum required tier.
 *
 * This is the single source of truth for "what does each tier include?".
 * Adding a future feature (e.g. ACT module, voice tutor, exam proctoring)
 * = one line in FEATURE_REQUIREMENTS. Anywhere the app gates that
 * feature, call canAccessFeature(plan, key) instead of hardcoding a tier.
 *
 * Grandfathering note: existing customers' tier (stored on User row)
 * doesn't change when new features are added. So if you ship ACT
 * support and map it to PRO here, every existing Pro user gets ACT
 * automatically with no per-user data change required. The "price
 * grandfather" stays on the User; the "feature set" lives here.
 */
export type FeatureKey =
  | 'aiTutor'
  | 'spacedRepetition'
  | 'pdfReports'
  | 'pdfReportPro'
  | 'printableTests'
  | 'detailedAnalytics'
  | 'studyPlan'
  | 'timeAnalytics'
  | 'mistakeDna'

export const FEATURE_REQUIREMENTS: Record<FeatureKey, PlanKey> = {
  // 0.4 re-tier (competitive-roadmap): Sage moved PREMIUM → PRO, metered (a
  // tighter daily cap for PRO than PREMIUM — TUTOR_DAILY_CAP in lib/tutor). The
  // AI tutor is the qualitative wedge; putting it at the $14.99 entry tier widens
  // reach, while the per-user monthly LLM ceiling + per-tier daily cap bound
  // COGS. PREMIUM keeps a much higher Sage cap + priority + early access.
  // Packaging v2: Sage is available to EVERYONE, metered by tier (Free 3/day ·
  // Pro 50/day · Admin unlimited — see TUTOR_DAILY_CAP). The gate is no longer
  // "can you use Sage at all" but "how many per day", so access is FREE.
  aiTutor: 'FREE',
  // Spaced-repetition review (/review) is FREE — a deliberate retention loop
  // that brings free users back daily (and warms them toward upgrading). The
  // page has never enforced a gate; this aligns the config to that reality.
  spacedRepetition: 'FREE',
  // Downloading a score report at all is FREE. It is the artifact a student
  // shows a parent after a test, and it was already shipping to everyone: the
  // results page rendered the download button unconditionally and nothing ever
  // called canAccessPDFReports. Declaring it PRO while shipping it free meant
  // the next person to read this file would believe a gate existed (#1767).
  pdfReports: 'FREE',
  // The RICH report is the paid one — the analysis layer, not the score.
  // See src/lib/pdf/pdf-report.ts for exactly which sections each tier gets.
  pdfReportPro: 'PRO',
  printableTests: 'PRO',
  // PRO is the entry point for analytics + study plan (was BASIC
  // pre-#420, but BASIC was retired in the 3-tier restructure).
  detailedAnalytics: 'PRO',
  studyPlan: 'PRO',
  // Per-question pacing analytics (avg time by topic/difficulty + trend).
  // Same tier as detailedAnalytics — PRO is the analytics entry point.
  timeAnalytics: 'PRO',
  // Mistake DNA — the deep, full-history breakdown of *how* you miss (careless
  // vs concept gap vs rushed vs running out of time) with the topics to drill.
  // The free dashboard "Patterns to watch" chip teases it; the full surface is
  // PRO, the analytics entry point.
  mistakeDna: 'PRO',
}

/**
 * Generic feature-access check. Prefer this over the per-feature
 * helpers below — those are kept as thin wrappers so existing call
 * sites continue to compile, but new gating code should use
 * canAccessFeature(plan, 'aiTutor') etc.
 */
export function canAccessFeature(plan: PlanKey, feature: FeatureKey): boolean {
  return hasPlanAccess(plan, FEATURE_REQUIREMENTS[feature])
}

// ---- Backwards-compatible wrappers ----
// Existing code uses these named helpers in many places. Keep them
// alive (they delegate to canAccessFeature) so we don't touch every
// call site in this PR. Future PRs can migrate call sites to
// canAccessFeature directly and eventually retire these.

export function canAccessAITutor(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'aiTutor')
}

export function canAccessSpacedRepetition(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'spacedRepetition')
}

export function canAccessPDFReports(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'pdfReports')
}

/** The rich report — analysis sections, not just the score. */
export function canAccessProPDFReport(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'pdfReportPro')
}

export function canAccessPrintableTests(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'printableTests')
}

export function canAccessDetailedAnalytics(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'detailedAnalytics')
}

export function canAccessStudyPlan(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'studyPlan')
}

export function canAccessTimeAnalytics(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'timeAnalytics')
}

export function canAccessMistakeDna(plan: PlanKey): boolean {
  return canAccessFeature(plan, 'mistakeDna')
}

/**
 * Map plan to the corresponding Stripe env var name.
 * FREE has no Stripe price. Returns undefined for FREE.
 */
export function stripePriceEnvVar(plan: PlanKey): string | undefined {
  switch (plan) {
    case 'PRO':
      return 'STRIPE_PRICE_ID_PRO'
    default:
      return undefined
  }
}

/**
 * Resolve the Stripe price ID for a given plan from env vars.
 * Falls back to the legacy STRIPE_PRICE_ID for PRO (backward compat).
 */
export function getStripePriceId(plan: PlanKey): string | undefined {
  const envVar = stripePriceEnvVar(plan)
  if (!envVar) return undefined

  const value = process.env[envVar]
  // Backward compat: if PRO-specific var is not set, use legacy STRIPE_PRICE_ID
  if (!value && plan === 'PRO') {
    return process.env.STRIPE_PRICE_ID
  }
  return value
}

// ---- Packaging v2: one Pro tier, priced by billing TERM ----
// The deal-by-commitment ladder. total is what Stripe charges per period; every
// other number on the card is DERIVED from it. Create 3 Prices on the one Pro
// Product and set the env vars below; until set, the checkout falls back to the
// monthly price.
//
// Why derived (audit 2026-09-14): the annual term was typed in as "$14.99/month,
// billed $179 per year". $14.99 x 12 is $179.88 and $179 / 12 is $14.92, so the
// card made two claims that could not both be true, and a buyer checking the
// headline against the charge found it. Hand-typed per-month figures and
// savings badges drift from the charge the moment one is edited; computing them
// from (total, months) makes that disagreement unrepresentable.

export type BillingInterval = 'monthly' | 'sixmo' | 'annual'

export type ProTerm = {
  interval: BillingInterval
  /** Months covered by one charge. */
  months: number
  /** What Stripe charges per billing period, in dollars. The source of truth. */
  total: number
  /** total / months, rounded to the cent. What we headline. */
  perMonth: number
  /** The exact charge and its cadence, e.g. "$179 billed every year". */
  totalLabel: string
  /** That the charge recurs, and how often. */
  renewalLabel: string
  /** Savings against paying monthly for the same months; null for monthly. */
  badge: string | null
}

/** "$179", "$24.99" — whole dollars drop the cents. */
export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2).replace(/\.00$/, '')}`
}

const TERM_PRICES: Record<BillingInterval, { months: number; total: number }> = {
  monthly: { months: 1, total: 24.99 },
  sixmo: { months: 6, total: 108 },
  annual: { months: 12, total: 179 },
}

const CADENCE: Record<BillingInterval, { billed: string; renews: string }> = {
  monthly: { billed: 'billed every month', renews: 'Renews automatically each month' },
  sixmo: { billed: 'billed every 6 months', renews: 'Renews automatically every 6 months' },
  annual: { billed: 'billed every year', renews: 'Renews automatically each year' },
}

/**
 * Percent saved versus the monthly price over the same months, rounded DOWN.
 * A savings claim may understate by a fraction of a point; it must never
 * overstate (6 months at $108 vs $149.94 is 27.97%, which is "Save 27%").
 */
export function savingsPercent(interval: BillingInterval): number {
  const { months, total } = TERM_PRICES[interval]
  const monthlyEquivalent = TERM_PRICES.monthly.total * months
  return Math.floor((1 - total / monthlyEquivalent) * 100 + 1e-9)
}

function buildTerm(interval: BillingInterval): ProTerm {
  const { months, total } = TERM_PRICES[interval]
  const saved = interval === 'monthly' ? 0 : savingsPercent(interval)
  return {
    interval,
    months,
    total,
    perMonth: Math.round((total / months) * 100) / 100,
    totalLabel: `${formatUsd(total)} ${CADENCE[interval].billed}`,
    renewalLabel: CADENCE[interval].renews,
    badge:
      saved <= 0 ? null : interval === 'annual' ? `Best value · Save ${saved}%` : `Save ${saved}%`,
  }
}

export const PRO_TERMS: Record<BillingInterval, ProTerm> = {
  monthly: buildTerm('monthly'),
  sixmo: buildTerm('sixmo'),
  annual: buildTerm('annual'),
}

export const BILLING_INTERVALS: BillingInterval[] = ['monthly', 'sixmo', 'annual']

export function isBillingInterval(v: unknown): v is BillingInterval {
  return v === 'monthly' || v === 'sixmo' || v === 'annual'
}

/**
 * Resolve the Stripe price ID for a Pro billing term. Monthly uses the existing
 * STRIPE_PRICE_ID_PRO (with the legacy STRIPE_PRICE_ID fallback); 6-month and
 * annual use their own env vars, falling back to monthly until configured so a
 * misconfigured deploy still sells *something* rather than 500ing.
 */
export function getStripePriceIdForInterval(interval: BillingInterval): string | undefined {
  const monthly = getStripePriceId('PRO')
  if (interval === 'sixmo') return process.env.STRIPE_PRICE_ID_PRO_6MO ?? monthly
  if (interval === 'annual') return process.env.STRIPE_PRICE_ID_PRO_ANNUAL ?? monthly
  return monthly
}

/** All paid plan keys (i.e. plans that need a Stripe checkout) */
export const PAID_PLANS: PlanKey[] = ['PRO']

/** Validate that a string is a valid PlanKey */
export function isValidPlan(value: unknown): value is PlanKey {
  return typeof value === 'string' && PLAN_ORDER.includes(value as PlanKey)
}

/** Validate that a string is a valid paid PlanKey */
export function isValidPaidPlan(value: unknown): value is PlanKey {
  return isValidPlan(value) && PAID_PLANS.includes(value as PlanKey)
}

/**
 * Per-tier DAILY Sage message cap (packaging v2). Sage is available to everyone,
 * metered by tier: FREE gets a 3/day taste (the quality wedge), PRO gets a 50/day
 * fair-use cap (per-message Sonnet cost ~$0.04, so 50/day stays under the $80/mo
 * LLM ceiling and covers any honest study day). ADMINs bypass the cap entirely
 * (see checkTutorRateLimit's isAdmin). Enforced in src/lib/tutor/tutor.ts; kept
 * here so copy can quote it.
 */
export const TUTOR_DAILY_CAP: Record<PlanKey, number> = { FREE: 3, PRO: 50 }
